import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { forkJoin } from 'rxjs';
import { CompanyTemplateService } from 'src/app/services/company-template.service';
import { CompanyDocumentChecklistService } from 'src/app/services/company-document-checklist.service';
import { UtilService } from 'src/app/services/util.service';
import { CommonSkeletonComponent } from 'src/app/skeleton/common-skeleton/common-skeleton.component';
import Swal from 'sweetalert2';
import { NgIconComponent } from '@ng-icons/core';


interface TemplateSelection {
  included: boolean;
  isMandatory: boolean;
  checklistId?: string;
  alreadyRequired: boolean;
}

interface TemplateGroup {
  category: string;
  expanded: boolean;
  templates: any[];
}

interface SupplierRow {
  template: any;
  checklist: any;
  upload: any;
  showVaultPanel: boolean;
  vaultDocs: any[];
  selectedVaultId: string;
  uploading: boolean;
  checkingVault: boolean;
}

@Component({
  selector: 'app-b2b-document-setup',
  standalone: true,
  imports: [CommonModule, FormsModule, CommonSkeletonComponent,
NgIconComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './b2b-document-setup.component.html',
  styleUrls: ['./b2b-document-setup.component.scss']
})
export class B2bDocumentSetupComponent implements OnInit {
  // Inputs via BsModalService initialState
  b2bContactId: string = '';
  myBusinessId: string = '';
  peerBusinessId: string = '';
  peerBusinessName: string = '';
  mode: 'customer' | 'supplier' = 'customer';

  // Customer mode state
  templateGroups: TemplateGroup[] = [];
  selections: { [comDocumentId: string]: TemplateSelection } = {};

  // Supplier mode state
  supplierRows: SupplierRow[] = [];

  // Shared state
  loading = true;
  saving = false;
  fileInputDocId: string = '';

  constructor(
    public bsModalRef: BsModalRef,
    private templateService: CompanyTemplateService,
    private checklistService: CompanyDocumentChecklistService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    if (this.mode === 'customer') {
      this.loadCustomerData();
    } else {
      this.loadSupplierData();
    }
  }

  // ─── Customer Mode ───────────────────────────────────────────────────────────

  loadCustomerData(): void {
    this.loading = true;
    this.templateService.searchTemplates({ businessId: this.myBusinessId, b2bContactId: this.b2bContactId } as any)
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res.code === '00000' || res.code === '22222') {
            const templates: any[] = res.data?.templates || [];
            const checklistMap: { [id: string]: any } = res.data?.templateChecklistMap || {};
            this.buildCustomerGroups(templates, checklistMap);
          }
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  buildCustomerGroups(templates: any[], checklistMap: { [id: string]: any }): void {
    const categoryMap: { [cat: string]: any[] } = {};
    for (const t of templates) {
      const cat = t.documentCategory || 'Other';
      if (!categoryMap[cat]) categoryMap[cat] = [];
      categoryMap[cat].push(t);
      const existing = checklistMap[t.comDocumentId];
      this.selections[t.comDocumentId] = {
        included: !!existing,
        isMandatory: existing ? !!existing.isMandatory : false,
        checklistId: existing?.checklistId,
        alreadyRequired: !!existing
      };
    }
    this.templateGroups = Object.keys(categoryMap).map(cat => ({
      category: cat,
      expanded: true,
      templates: categoryMap[cat]
    }));
  }

  toggleGroup(group: TemplateGroup): void {
    group.expanded = !group.expanded;
  }

  get hasAnySelection(): boolean {
    return Object.values(this.selections).some(s => s.included);
  }

  saveCustomer(): void {
    const userId = localStorage.getItem('userId') || this.myBusinessId;
    const creates: any[] = [];
    const updates: any[] = [];

    for (const group of this.templateGroups) {
      for (const t of group.templates) {
        const sel = this.selections[t.comDocumentId];
        if (!sel) continue;

        if (sel.included && !sel.alreadyRequired) {
          creates.push({ template: t, isMandatory: sel.isMandatory });
        } else if (sel.included && sel.alreadyRequired && sel.checklistId) {
          updates.push({ checklistId: sel.checklistId, isMandatory: sel.isMandatory });
        }
      }
    }

    if (creates.length === 0 && updates.length === 0) {
      this.bsModalRef.hide();
      return;
    }

    this.saving = true;
    this.util.startLoader();

    const createObs = creates.map(item =>
      this.checklistService.createChecklist({
        b2bContactId: this.b2bContactId,
        demanderBusinessId: this.myBusinessId,
        uploaderBusinessId: this.peerBusinessId,
        comDocumentId: item.template.comDocumentId,
        documentName: item.template.documentName,
        documentCategory: item.template.documentCategory,
        isMandatory: item.isMandatory,
        hasExpiry: item.template.hasExpiry,
        defaultExpiryMonths: item.template.defaultExpiryMonths,
        allowedFileTypes: item.template.allowedFileTypes,
        maxFileSizeMB: item.template.maxFileSizeMB,
        fieldSchema: item.template.fieldSchema,
        createdBy: userId
      })
    );

    const updateObs = updates.map(item =>
      this.checklistService.updateChecklist({ checklistId: item.checklistId, isMandatory: item.isMandatory })
    );

    const allObs = [...createObs, ...updateObs];

    forkJoin(allObs).subscribe({
      next: () => {
        this.saving = false;
        this.util.stopLoader();
        Swal.fire({
          icon: 'success',
          title: 'Document requirements saved',
          showConfirmButton: false,
          timer: 2000
        }).then(() => this.bsModalRef.hide());
      },
      error: () => {
        this.saving = false;
        this.util.stopLoader();
        Swal.fire('Error', 'Failed to save document requirements. Please try again.', 'error');
      }
    });
  }

  skipCustomer(): void {
    this.bsModalRef.hide();
  }

  // ─── Supplier Mode ────────────────────────────────────────────────────────────

  loadSupplierData(): void {
    this.loading = true;
    forkJoin({
      templates: this.templateService.searchTemplates({ businessId: this.peerBusinessId, b2bContactId: this.b2bContactId } as any),
      uploads: this.checklistService.searchUploads({ b2bContactId: this.b2bContactId, businessId: this.myBusinessId })
    }).subscribe({
      next: ({ templates, uploads }: any) => {
        this.loading = false;
        const allTemplates: any[] = templates.data?.templates || [];
        const checklistMap: { [id: string]: any } = templates.data?.templateChecklistMap || {};
        const allUploads: any[] = uploads.data?.uploads || [];

        // Build upload lookup by checklistId
        const uploadByChecklistId: { [id: string]: any } = {};
        for (const u of allUploads) {
          uploadByChecklistId[u.checklistId] = u;
        }

        this.supplierRows = allTemplates
          .filter((t: any) => checklistMap[t.comDocumentId])
          .map((t: any) => {
            const checklist = checklistMap[t.comDocumentId];
            return {
              template: t,
              checklist,
              upload: uploadByChecklistId[checklist.checklistId] || null,
              showVaultPanel: false,
              vaultDocs: [],
              selectedVaultId: '',
              uploading: false,
              checkingVault: false
            };
          });
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  getUploadStatus(row: SupplierRow): string {
    if (!row.upload) return 'NOT_UPLOADED';
    return row.upload.uploadStatus || 'SUBMITTED';
  }

  onUploadClick(row: SupplierRow, fileInput: HTMLInputElement): void {
    row.checkingVault = true;
    this.checklistService.checkVault(this.myBusinessId, row.template.comDocumentId).subscribe({
      next: (res: any) => {
        row.checkingVault = false;
        const vaultDocs = res.data?.vaultDocuments || [];
        if (vaultDocs.length > 0) {
          row.vaultDocs = vaultDocs;
          row.selectedVaultId = '';
          row.showVaultPanel = true;
        } else {
          fileInput.click();
        }
      },
      error: () => {
        row.checkingVault = false;
        fileInput.click();
      }
    });
  }

  useVaultDoc(row: SupplierRow): void {
    if (!row.selectedVaultId) return;
    const uploadedBy = localStorage.getItem('userId') || this.myBusinessId;
    row.uploading = true;
    this.checklistService.mapVaultDocument(row.checklist.checklistId, row.selectedVaultId, uploadedBy, this.b2bContactId)
      .subscribe({
        next: (res: any) => {
          row.uploading = false;
          row.showVaultPanel = false;
          if (res.code === '00000') {
            row.upload = { uploadStatus: 'SUBMITTED' };
            Swal.fire({ icon: 'success', title: 'Document uploaded', showConfirmButton: false, timer: 2000 });
          }
        },
        error: () => {
          row.uploading = false;
          Swal.fire('Error', 'Upload failed. Please try again.', 'error');
        }
      });
  }

  cancelVaultPanel(row: SupplierRow, fileInput: HTMLInputElement): void {
    row.showVaultPanel = false;
    fileInput.click();
  }

  onFileSelected(event: Event, row: SupplierRow): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) return;
    const file = input.files[0];
    const uploadedBy = localStorage.getItem('userId') || this.myBusinessId;
    row.uploading = true;
    this.checklistService.uploadDocument(row.checklist.checklistId, file, uploadedBy, this.b2bContactId)
      .subscribe({
        next: (res: any) => {
          row.uploading = false;
          input.value = '';
          if (res.code === '00000') {
            row.upload = { uploadStatus: 'SUBMITTED' };
            Swal.fire({ icon: 'success', title: 'Document uploaded', showConfirmButton: false, timer: 2000 });
          } else {
            Swal.fire('Error', res.message || 'Upload failed. Please try again.', 'error');
          }
        },
        error: () => {
          row.uploading = false;
          input.value = '';
          Swal.fire('Error', 'Upload failed. Please try again.', 'error');
        }
      });
  }

  closeSupplier(): void {
    this.bsModalRef.hide();
  }
}
